import {
  Button,
  Card,
  CardBody,
  chakra,
  Code,
  Divider,
  Flex,
  HStack,
  Text,
} from "@chakra-ui/react";
import { RiBarChartLine, RiFlashlightLine } from "@remixicon/react";
import React from "react";
import { Link as RouterLink } from "react-router";
import { AxisInterval } from "recharts/types/util/types";

import SimpleBarChart from "@/common/charts/components/SimpleBarChart";
import InfiniteAnimation from "@/common/components/InfiniteAnimation";
import InfoIconTooltip from "@/common/components/InfoIconTooltip";
import PeriodPicker, { Period } from "@/common/components/PeriodPicker";
import { SectionHeading } from "@/common/components/SectionHeading";
import { useEnvironment } from "@/common/contexts/EnvironmentContext";
import { useSlugParam } from "@/common/hooks/useParam";
import { useSearchParam } from "@/common/hooks/useSearchParam";
import { formatLargeNumber } from "@/common/utils/numberFormatting";
import { periodToDates } from "@/common/utils/periodToDates";
import useAllSegment from "@/company/data/useAllSegment";
import { EmptyMonitorState } from "@/feature/components/EmptyMonitorState";
import { useFeatureAdoptionData } from "@/feature/data/useFeatureAdoptionData";
import { useFeatureRolloutStatusData } from "@/feature/data/useFeatureRolloutStatusData";
import { useFeatureAdoptionPlaceholderData } from "@/feature/data/useMonitorPlaceholderData";
import { useAdoptionChartFormatters } from "@/feature/hooks/useAdoptionChartFormatters";
import { MONITOR_CARD_HEIGHT } from "@/feature/pages/FeatureMonitor";
import { useFlag } from "@reflag/react-sdk";
import { AdoptionChartData, Granularity } from "@reflag/shared/featureAPI";
import { SegmentUrl } from "@reflag/shared/urls";

const AdoptionIcon = chakra(RiBarChartLine, {
  baseStyle: {
    boxSize: 4,
    _dark: {
      color: "gray.500",
    },
    color: "gray.400",
  },
});

type AdoptionChartProps = {
  title: string;
  description?: string;
  buttonText?: string;
  data?: AdoptionChartData;
  featureId: string;
  metric: "exposureCount" | "usageCount";
  granularity: Granularity;
  period: Period;
  tooltip?: React.ReactNode;
};

function AdoptionChart({
  featureId,
  data,
  title,
  description,
  buttonText,
  metric,
  granularity,
  period,
  tooltip,
}: AdoptionChartProps) {
  const { env } = useEnvironment();
  const allSegment = useAllSegment();
  const exploreLink = SegmentUrl(env, allSegment!, {
    filter: {
      type: "featureMetric",
      operator: "GT",
      values: ["0"],
      metric,
      featureId,
    },
    column: [`featureMetrics.${featureId}.${metric}`],
  });

  const { formatter, tooltipFormatter } =
    useAdoptionChartFormatters(granularity);

  const interval: AxisInterval = period === "past7days" ? 24 : "preserveEnd";

  return (
    <Flex
      direction="row"
      gap={4}
      h={MONITOR_CARD_HEIGHT}
      isolation="isolate"
      position="relative"
      px={8}
      py={6}
    >
      <Flex direction="column" justify="space-between" w={64}>
        <Flex direction="column" gap={2}>
          <HStack>
            <Text fontSize="lg">{title}</Text>
          </HStack>
          {description && (
            <HStack spacing={1}>
              <Text color="dimmed" fontSize="xs">
                {description}
              </Text>
              {tooltip && <InfoIconTooltip text={tooltip} />}
            </HStack>
          )}
        </Flex>
        <Flex direction="column" gap={3}>
          <Flex direction="column" gap={0.5}>
            <HStack align="baseline">
              <Text as="span" fontSize="2xl" fontWeight="medium">
                {formatLargeNumber(data?.companies ?? 0)}
              </Text>{" "}
              <Text as="span" color="dimmed" fontSize="lg">
                companies
              </Text>
            </HStack>
            <HStack align="baseline">
              <Text as="span" fontSize="lg" fontWeight="medium">
                {formatLargeNumber(data?.users ?? 0)}
              </Text>{" "}
              <Text as="span" color="dimmed" fontSize="md">
                users
              </Text>
            </HStack>
          </Flex>
          <Button
            as={RouterLink}
            mt={2}
            size="xs"
            to={exploreLink}
            variant="outline"
            width="fit-content"
          >
            {buttonText ?? `Explore ${title.toLowerCase()} companies`}
          </Button>
        </Flex>
      </Flex>
      <Flex flexGrow={1}>
        <SimpleBarChart
          data={data?.timeseries ?? []}
          dataKeys={[
            {
              key: "value",
              name: "Distinct companies",
              color: "primary",
            },
          ]}
          height={200}
          isAnimationActive={false}
          tooltip={{
            labelFormatter: tooltipFormatter ?? formatter,
          }}
          xAxis={{
            tickLine: false,
            minTickGap: 15,
            tickFormatter: formatter,
            interval,
          }}
          yAxis={{
            padding: {
              top: 10,
            },
            tickMargin: 10,
            axisLine: false,
          }}
        />
      </Flex>
    </Flex>
  );
}

const allowedPeriods = ["pastHour", "past7days", "past30days"] as const;

function getGranularity(period: Period): Granularity {
  switch (period) {
    case "pastHour":
      return "minute";
    case "past7days":
      return "hour";
    case "past30days":
      return "day";
    default:
      return "week";
  }
}

export const FeatureAdoption = () => {
  const { featureId } = useSlugParam("feature");
  const { isEnabled: adoptionTimepicker } = useFlag("adoption-timepicker");

  const [period, setPeriod] = useSearchParam<Period>("period", {
    fallback: "pastHour",
  });
  const live = !adoptionTimepicker || period === "pastHour";

  const { startDate, endDate } = periodToDates(period);
  const params = {
    startTimestamp: startDate.toISOString(),
    endTimestamp: endDate.toISOString(),
    granularity: getGranularity(period),
  };
  const placeholderData = useFeatureAdoptionPlaceholderData(params);
  const {
    data: adoption,
    isFetching: isAdoptionFetching,
    isLoading: isAdoptionLoading,
    isError: isAdoptionError,
    refetch: refetchAdoption,
  } = useFeatureAdoptionData(featureId!, params, live);
  const {
    data: rollout,
    isLoading: isRolloutLoading,
    isError: isRolloutError,
  } = useFeatureRolloutStatusData(featureId!);

  const isLoading = isAdoptionLoading || isRolloutLoading;
  const isError = isAdoptionError || isRolloutError;

  const hasCheckData = rollout?.statuses.some(({ latestCheck }) => latestCheck);
  const hasUsageData = rollout?.statuses.some(({ latestUsage }) => latestUsage);

  return (
    <Flex direction="column" gap={4}>
      <SectionHeading
        fontSize="lg"
        icon={<AdoptionIcon />}
        isLoading={isAdoptionFetching}
        rightElement={
          <HStack spacing={4}>
            {live && (
              <HStack spacing={1}>
                <InfiniteAnimation>
                  <RiFlashlightLine size={12} />
                </InfiniteAnimation>
                <Text fontSize="sm" fontWeight="medium">
                  Live
                </Text>
              </HStack>
            )}
            {adoptionTimepicker && (
              <PeriodPicker
                allowedPeriods={allowedPeriods}
                value={period}
                onChange={setPeriod}
              />
            )}
          </HStack>
        }
      >
        Adoption
      </SectionHeading>

      <Card>
        <CardBody display="flex" flexDirection="column" p={0}>
          <EmptyMonitorState
            hasData={hasCheckData}
            isError={isError}
            isLoading={isLoading}
            minH={MONITOR_CARD_HEIGHT}
            noDataText="No exposure data yet"
            usecase={"access"}
            onRetry={refetchAdoption}
          >
            <AdoptionChart
              data={hasCheckData ? adoption?.exposed : placeholderData.exposed}
              description="Access check that resulted in enabled"
              featureId={featureId!}
              granularity={getGranularity(period)}
              metric="exposureCount"
              period={period}
              title="Exposed"
              tooltip={
                <>
                  <Text>
                    Access checks are automatically collected when{" "}
                    <Code>isEnabled</Code> is checked through SDKs.
                  </Text>
                  <br />
                  <Text>
                    When at least 1 access check result in <Code>enabled</Code>,
                    we consider the company exposed to the flag.
                  </Text>
                </>
              }
            />
          </EmptyMonitorState>
          <Divider />
          <EmptyMonitorState
            hasData={hasUsageData}
            isError={isError}
            isLoading={isLoading}
            minH={MONITOR_CARD_HEIGHT}
            noDataText="No tracking data yet"
            title="Setup tracking"
            usecase={"usage"}
            onRetry={refetchAdoption}
          >
            <AdoptionChart
              buttonText="Explore tracked companies"
              data={hasUsageData ? adoption?.used : placeholderData.used}
              description="Track event"
              featureId={featureId!}
              granularity={getGranularity(period)}
              metric="usageCount"
              period={period}
              title="Tracked"
              tooltip={
                <>
                  <Text>
                    Track events are used for tracking meaningful flag
                    interactions like clicking a button. At least 1 track event
                    is required to be considered tracked.
                  </Text>
                  <br />
                  <Text>
                    Use the <Code>track</Code> SDK method to collect them.
                  </Text>
                </>
              }
            />
          </EmptyMonitorState>
        </CardBody>
      </Card>
    </Flex>
  );
};
